import React, { useEffect, useState, useRef } from 'react';
import { getFirestore, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { app } from '../firebase';
import { fetchGuildMembers } from '../services/guildApi';
import {
  API_CONFIG,
  getApiUrl,
  GUILD_REGION,
  isCurrentMidnightSeasonSlug,
  isMythicSeasonSlug,
  MYTHIC_MIDNIGHT_CURRENT_SEASON_SLUG,
  MYTHIC_MIDNIGHT_LEADERBOARD_ENDPOINT,
  MYTHIC_MIDNIGHT_S1_SEASON_SLUG,
  profileRealmMatchesGuildRow,
} from '../config/apiConfig';

interface RaiderIOMember {
  name: string;
  realm: string;
  class: string;
  spec: string;
  mythic_plus_score: number;
  profile_url: string;
  rank: number; // Added rank to the interface
}

const PAGE_SIZE = 20;

// Leaderboard period: expansion + season (The War Within + Midnight S1 archived, Midnight S2 current)
type LeaderboardPeriod = 'tww_s2' | 'tww_s3' | 'midnight_s1' | 'midnight_s2';

const MYTHICPLUS_PERIOD_STORAGE_KEY = 'mythicplusLeaderboardPeriod';
/** Bump when the default current season changes so returning visitors get the new tab once. */
const MYTHICPLUS_PERIOD_MIGRATION_KEY = 'mythicplusLeaderboardPeriod_midnight_s2_default_v1';

function isLeaderboardPeriod(value: string | null): value is LeaderboardPeriod {
  return value === 'tww_s2' || value === 'tww_s3' || value === 'midnight_s1' || value === 'midnight_s2';
}

function getInitialLeaderboardPeriod(): LeaderboardPeriod {
  try {
    if (typeof localStorage === 'undefined') return 'midnight_s2';
    if (!localStorage.getItem(MYTHICPLUS_PERIOD_MIGRATION_KEY)) {
      localStorage.setItem(MYTHICPLUS_PERIOD_MIGRATION_KEY, '1');
      localStorage.setItem(MYTHICPLUS_PERIOD_STORAGE_KEY, 'midnight_s2');
      return 'midnight_s2';
    }
    const saved = localStorage.getItem(MYTHICPLUS_PERIOD_STORAGE_KEY);
    if (isLeaderboardPeriod(saved)) return saved;
  } catch {
    /* ignore */
  }
  return 'midnight_s2';
}

const PERIOD_LABELS: Record<LeaderboardPeriod, { expansion: string; season: string; isCurrent: boolean }> = {
  tww_s2: { expansion: 'The War Within', season: 'Season 2', isCurrent: false },
  tww_s3: { expansion: 'The War Within', season: 'Season 3', isCurrent: false },
  midnight_s1: { expansion: 'Midnight', season: 'Season 1', isCurrent: false },
  midnight_s2: { expansion: 'Midnight', season: 'Season 2', isCurrent: true },
};

const FIRESTORE_SEASON_COLLECTIONS: Record<Exclude<LeaderboardPeriod, 'midnight_s2'>, string> = {
  tww_s2: 'mythicplus_leaderboard_season2',
  tww_s3: 'mythicplus_leaderboard_season3',
  midnight_s1: 'mythicplus_leaderboard_midnight_s1',
};

function getMythicRankRowSeasonRaw(player: any): string | null {
  const raw =
    player.score_season ??
    player.scoreSeason ??
    player.mythic_season ??
    player.MythicSeason ??
    player.season ??
    player.Season;
  if (raw == null || raw === '') return null;
  return String(raw);
}

/**
 * If the API tags each row with a score season, keep only rows for `seasonSlug`.
 * If no row has a season field, trust the response root `mythic_season` — when it matches,
 * use all rows so the leaderboard loads and character-profiles refresh can run.
 */
function filterMythicRankRowsForSeason(apiPayload: any, seasonSlug: string): any[] {
  const rows = apiPayload?.data;
  if (!Array.isArray(rows)) return [];
  const rootMatches = isMythicSeasonSlug(apiPayload.mythic_season ?? apiPayload.mythicSeason, seasonSlug);
  const anyRowHasSeason = rows.some((p) => getMythicRankRowSeasonRaw(p) != null);
  if (!anyRowHasSeason) {
    return rootMatches ? rows : [];
  }
  return rows.filter((p) => isMythicSeasonSlug(getMythicRankRowSeasonRaw(p), seasonSlug));
}

const PROFILE_FETCH_CONCURRENCY = 8;

/**
 * mythic-rank rows can still carry stale MythicScore while character-profiles matches Raider.io.
 * Refresh scores from character-profiles (same source as roster) so the Midnight tab matches /character-profiles/{name}.
 */
async function replaceMidnightScoresFromCharacterProfiles(
  members: RaiderIOMember[],
  signal: AbortSignal
): Promise<RaiderIOMember[]> {
  const out: RaiderIOMember[] = [];
  for (let i = 0; i < members.length; i += PROFILE_FETCH_CONCURRENCY) {
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const chunk = members.slice(i, i + PROFILE_FETCH_CONCURRENCY);
    const batch = await Promise.all(
      chunk.map(async (m) => {
        try {
          const url = getApiUrl(
            `character-profiles/${encodeURIComponent(m.name)}?realm=${encodeURIComponent(m.realm)}&region=${encodeURIComponent(GUILD_REGION)}`
          );
          const res = await fetch(url, {
            headers: { 'X-API-Key': API_CONFIG.API_KEY },
            method: 'GET',
            mode: 'cors',
            signal,
          });
          if (!res.ok) return m;
          const json = await res.json();
          const d = json.data;
          if (!d) return m;
          if (!profileRealmMatchesGuildRow(d.realm, m.realm)) return m;
          const profSeason = d.score_season ?? d.scoreSeason ?? d.mythic_season ?? d.season;
          if (profSeason != null && profSeason !== '' && !isCurrentMidnightSeasonSlug(profSeason)) {
            return null;
          }
          if (typeof d.MythicScore === 'number') {
            return { ...m, mythic_plus_score: d.MythicScore };
          }
        } catch (e: unknown) {
          const name = e instanceof Error ? e.name : '';
          if (name === 'AbortError') throw e;
        }
        return m;
      })
    );
    for (const row of batch) {
      if (row != null) out.push(row);
    }
  }
  return out;
}

/** Higher score first; tied scores sorted A–Z by character name, then realm. */
function compareLeaderboardMembers(a: RaiderIOMember, b: RaiderIOMember): number {
  const scoreDiff = b.mythic_plus_score - a.mythic_plus_score;
  if (scoreDiff !== 0) return scoreDiff;
  const nameCmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  if (nameCmp !== 0) return nameCmp;
  return (a.realm || '').localeCompare(b.realm || '', undefined, { sensitivity: 'base' });
}

function transformMythicRankRows(currentData: any[]): RaiderIOMember[] {
  const transformedData = currentData.map((player: any, index: number) => {
    let extractedName = 'Unknown';
    let extractedRealm = 'Unknown';

    if (player.ProfileURL) {
      const urlParts = player.ProfileURL.split('/');
      if (urlParts.length >= 7) {
        extractedName = urlParts[6];
        extractedRealm = urlParts[5];
      }
    }

    return {
      name: player.Name || player.name || extractedName,
      realm: player.Realm || player.realm || extractedRealm,
      class: player.character_class || 'Unknown',
      spec: player.spec || player.character_class || player.class || 'Unknown',
      mythic_plus_score: player.MythicScore || 0,
      profile_url: player.ProfileURL || getApiUrl(`character-profiles/${player.Name || player.name}`),
      rank: player.Rank || (index + 1),
    };
  });

  const uniquePlayers = new Map<string, RaiderIOMember>();
  transformedData.forEach((player: RaiderIOMember) => {
    const key = `${player.name.toLowerCase()}|${player.realm.toLowerCase()}`;
    if (!uniquePlayers.has(key) || player.mythic_plus_score > uniquePlayers.get(key)!.mythic_plus_score) {
      uniquePlayers.set(key, player);
    }
  });

  const deduplicatedData = Array.from(uniquePlayers.values()).sort(compareLeaderboardMembers);
  deduplicatedData.forEach((row, i) => {
    row.rank = i + 1;
  });
  return deduplicatedData;
}

// Helper to capitalize class names
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// Helper to generate the correct spec icon path (all lowercase, no spaces or hyphens)
function toSpecIconPath(className: string, spec: string, ext: string = 'png') {
  return `/images/spec/${className.toLowerCase().replace(/ |-/g, '')}/${spec.toLowerCase().replace(/ |-/g, '')}.${ext}`;
}

// SpecIcon component to handle .png/.jpg fallback
const SpecIcon: React.FC<{ className: string; spec: string }> = ({ className, spec }) => {
  const [imgSrc, setImgSrc] = React.useState(toSpecIconPath(className, spec, 'webp'));
  return (
    <img
      src={imgSrc}
      alt={`${spec} icon`}
      className="w-6 h-6 inline-block"
      onError={e => {
        if (imgSrc.endsWith('.webp')) {
          setImgSrc(toSpecIconPath(className, spec, 'png'));
        } else if (imgSrc.endsWith('.png')) {
          setImgSrc(toSpecIconPath(className, spec, 'jpg'));
        } else {
          (e.target as HTMLImageElement).style.display = 'none';
        }
      }}
    />
  );
};

const MythicPlus: React.FC = () => {
  const [members, setMembers] = useState<RaiderIOMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<LeaderboardPeriod>(getInitialLeaderboardPeriod);
  const [guildMembers, setGuildMembers] = useState<Set<string>>(new Set());
  const hasRestoredPage = useRef(false);

  const isHistorical = !PERIOD_LABELS[selectedPeriod].isCurrent;

  // Filtered members based on search and guild membership
  const filteredMembers = members
    .filter(m => {
      // Exclude specific characters who have left the guild but still appear in API data
      const excludedCharacters = ['deeoni', 'rentwu', 'sigyr', 'joniquaza', 'Jadedxoxo', 'Tâsât', 'Extolled', 'Lesliewar'];
      const excludedLower = excludedCharacters.map(c => c.toLowerCase());
      if (excludedLower.includes(m.name.toLowerCase())) {
        return false;
      }

      // Historical periods: no guild filter, search only
      if (isHistorical) {
        return m.name.toLowerCase().includes(search.toLowerCase()) ||
               (m.realm && m.realm.toLowerCase().includes(search.toLowerCase()));
      }

      // Current period (Midnight S2): apply guild filter
      if (!m.name || !m.realm) {
        return false;
      }
      const isGuildMember = guildMembers.has(`${m.name.toLowerCase()}|${m.realm.toLowerCase()}`);
      if (!isGuildMember) {
        return false;
      }

      return m.name.toLowerCase().includes(search.toLowerCase()) ||
             (m.realm && m.realm.toLowerCase().includes(search.toLowerCase()));
    })
    .sort(compareLeaderboardMembers)
    .map((member, index) => ({
      ...member,
      rank: index + 1, // Re-rank the filtered members starting from 1
    }));
  
  const totalPages = Math.ceil(filteredMembers.length / PAGE_SIZE);
  const pagedMembers = filteredMembers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Restore page from localStorage only on first mount
  useEffect(() => {
    if (!hasRestoredPage.current) {
      const saved = localStorage.getItem('mythicplusPage');
      if (saved) {
        const savedPage = parseInt(saved, 10);
        if (savedPage > 0 && savedPage <= totalPages) {
          setPage(savedPage);
        }
      }
      hasRestoredPage.current = true;
    }
    // eslint-disable-next-line
  }, [totalPages]);

  // Save page to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('mythicplusPage', String(page));
  }, [page]);

  // Remember expansion/season tab (after migration, default is Midnight Season 2)
  useEffect(() => {
    try {
      localStorage.setItem(MYTHICPLUS_PERIOD_STORAGE_KEY, selectedPeriod);
    } catch {
      /* ignore */
    }
  }, [selectedPeriod]);

  // Fetch current guild members for filtering
  useEffect(() => {
    const fetchGuildMembersForFiltering = async () => {
      try {
        const allGuildMembers = await fetchGuildMembers();
        
        // Create a lookup set for efficient filtering - include all guild members regardless of rank
        const memberSet = new Set<string>();
        allGuildMembers.forEach(member => {
          memberSet.add(`${member.name.toLowerCase()}|${member.realm.toLowerCase()}`);
        });
        
        setGuildMembers(memberSet);
      } catch (error) {
        console.error('Failed to fetch guild members for filtering:', error);
        // Don't set error state here as it's not critical for the leaderboard to function
      }
    };

    fetchGuildMembersForFiltering();
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    const fetchLeaderboard = async () => {
      setLoading(true);
      setError('');
      try {
        if (selectedPeriod === 'midnight_s2') {
          // Midnight Season 2: live from API (current season)
          const url = getApiUrl(MYTHIC_MIDNIGHT_LEADERBOARD_ENDPOINT);

          let response;
          try {
            response = await fetch(url, {
              headers: { 'X-API-Key': API_CONFIG.API_KEY },
              method: 'GET',
              mode: 'cors',
              signal: ac.signal,
            });
          } catch (fetchError: any) {
            const errorName = fetchError?.constructor?.name || '';
            const errorMessage = fetchError?.message || '';
            console.error('Network error fetching leaderboard:', fetchError);
            if (errorName === 'TimeoutError' || errorMessage.includes('signal timed out') || errorMessage.includes('timeout')) {
              console.error('Timeout: API did not respond within 60 seconds.');
            } else if (errorMessage?.includes('CERT') || errorMessage?.includes('certificate') || errorMessage?.includes('ERR_CERT_DATE_INVALID')) {
              console.error('SSL certificate error: API certificate expired or invalid.');
            } else {
              console.error('Network/fetch error:', errorMessage || 'Failed to fetch data from API');
            }
            if (fetchError?.name === 'AbortError') return;
            setError('API connection failed');
            setLoading(false);
            return;
          }
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('API Error Response:', response.status, response.statusText, errorText);
            setError('API connection failed');
            setLoading(false);
            return;
          }
          
          const data = await response.json();
          if (ac.signal.aborted) return;
          if (data.data && Array.isArray(data.data)) {
            const currentData = filterMythicRankRowsForSeason(data, MYTHIC_MIDNIGHT_CURRENT_SEASON_SLUG);
            const deduplicatedData = transformMythicRankRows(currentData);

            let finalData = deduplicatedData;
            if (guildMembers.size > 0) {
              finalData = deduplicatedData.filter(player => {
                if (!player.name || !player.realm) {
                  return false;
                }
                return guildMembers.has(`${player.name.toLowerCase()}|${player.realm.toLowerCase()}`);
              });
            }

            let rows = finalData;
            if (rows.length > 0) {
              try {
                rows = await replaceMidnightScoresFromCharacterProfiles(rows, ac.signal);
              } catch (enrichErr: unknown) {
                const nm = enrichErr instanceof Error ? enrichErr.name : '';
                if (nm === 'AbortError') return;
                console.warn('Midnight leaderboard: using mythic-rank scores; character-profiles refresh failed.', enrichErr);
              }
            }
            if (ac.signal.aborted) return;
            rows.sort(compareLeaderboardMembers);
            rows.forEach((row, idx) => {
              row.rank = idx + 1;
            });
            setMembers(rows);
          } else {
            console.error('Data structure unexpected:', data);
            setError('API connection failed');
          }
        } else {
          const collectionName = FIRESTORE_SEASON_COLLECTIONS[selectedPeriod];
          let archived: RaiderIOMember[] | null = null;
          try {
            const db = getFirestore(app);
            const q = query(collection(db, collectionName), orderBy('rank'));
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
              archived = snapshot.docs.map(doc => doc.data() as RaiderIOMember);
            }
          } catch (fsErr) {
            if (selectedPeriod !== 'midnight_s1') throw fsErr;
            console.warn('Midnight Season 1 Firestore archive unavailable; trying API.', fsErr);
          }

          if (archived) {
            if (ac.signal.aborted) return;
            setMembers(archived);
          } else if (selectedPeriod === 'midnight_s1') {
            // Firestore snapshot not written yet: show last Season 1 rows if the API still tags them.
            const url = getApiUrl(MYTHIC_MIDNIGHT_LEADERBOARD_ENDPOINT);
            const response = await fetch(url, {
              headers: { 'X-API-Key': API_CONFIG.API_KEY },
              method: 'GET',
              mode: 'cors',
              signal: ac.signal,
            });
            if (!response.ok) {
              setError('No archived data for Midnight Season 1 yet. Backfill this collection to view historical scores.');
              setMembers([]);
              return;
            }
            const data = await response.json();
            if (ac.signal.aborted) return;
            const s1Rows = transformMythicRankRows(
              filterMythicRankRowsForSeason(data, MYTHIC_MIDNIGHT_S1_SEASON_SLUG)
            );
            if (s1Rows.length === 0) {
              setError('No archived data for Midnight Season 1 yet. Backfill this collection to view historical scores.');
              setMembers([]);
            } else {
              setMembers(s1Rows);
            }
          } else {
            console.warn(`${collectionName} is empty`);
            setError(
              selectedPeriod === 'tww_s3'
                ? 'No archived data for The War Within Season 3 yet. Backfill this collection to view historical scores.'
                : 'Failed to load data.'
            );
            if (ac.signal.aborted) return;
            setMembers([]);
          }
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Leaderboard fetch error:', err);
        if (selectedPeriod === 'midnight_s2') {
          if (err.message?.includes('CERT') || err.message?.includes('certificate')) {
            console.error('SSL certificate error:', err.message);
          } else if (err.message?.includes('Failed to fetch')) {
            console.error('Network/fetch error:', err.message);
          } else {
            console.error('API error:', err.message);
          }
          setError('API connection failed');
        } else {
          if (err.code === 'permission-denied') {
            console.error('Firestore permission denied:', err.message);
          } else {
            console.error('Firestore error:', err.message);
          }
          setError('Failed to load data.');
        }
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    };
    fetchLeaderboard();
    return () => ac.abort();
  }, [selectedPeriod, guildMembers]);

  // Reset to page 1 if search changes or period changes
  useEffect(() => {
    setPage(1);
    localStorage.setItem('mythicplusPage', '1');
  }, [search, selectedPeriod]);

  return (
    <div className="page-container">
      <div className="max-w-5xl mx-auto px-4 pb-12">
      <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">
        <span className="gradient-text">Mythic+ Leaderboard</span>
      </h1>
      <p className="text-center text-text-secondary mb-8 max-w-2xl mx-auto">
        Live Mythic+ scores for guild members. <strong className="text-cyan-300">Midnight Season 2</strong> is current (live from API). <strong className="text-gray-400">Midnight Season 1</strong> and <strong className="text-gray-400">The War Within</strong> seasons are archived historical data.
      </p>

      {/* Expansion / Season Selection */}
      <div className="flex flex-col items-center gap-4 mb-6">
        <div className="flex flex-wrap justify-center gap-2">
          <span className="text-sm text-gray-500 self-center mr-2">The War Within (archived):</span>
          <div className="flex rounded-lg overflow-hidden">
            <button
              onClick={() => setSelectedPeriod('tww_s2')}
              className={`px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                selectedPeriod === 'tww_s2' ? 'bg-cyan-400 text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              Season 2
            </button>
            <button
              onClick={() => setSelectedPeriod('tww_s3')}
              className={`px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                selectedPeriod === 'tww_s3' ? 'bg-cyan-400 text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              Season 3
            </button>
          </div>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <span className="text-sm text-gray-500 self-center mr-2">Midnight:</span>
          <div className="flex rounded-lg overflow-hidden">
            <button
              onClick={() => setSelectedPeriod('midnight_s1')}
              className={`px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                selectedPeriod === 'midnight_s1' ? 'bg-cyan-400 text-black' : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              Season 1
            </button>
            <button
              onClick={() => setSelectedPeriod('midnight_s2')}
              className={`px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                selectedPeriod === 'midnight_s2' ? 'bg-green-500 text-white' : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              Season 2 <span className="text-green-300">●</span> Live
            </button>
          </div>
        </div>
      </div>

      {/* Period status badge */}
      <div className="flex justify-center mb-6">
        <div className={`px-4 py-2 rounded-full font-medium text-sm ${
          PERIOD_LABELS[selectedPeriod].isCurrent ? 'bg-green-500 text-white' : 'bg-gray-600 text-white'
        }`}>
          {PERIOD_LABELS[selectedPeriod].isCurrent
            ? `Current — ${PERIOD_LABELS[selectedPeriod].expansion} ${PERIOD_LABELS[selectedPeriod].season}`
            : `Historical — ${PERIOD_LABELS[selectedPeriod].expansion} ${PERIOD_LABELS[selectedPeriod].season}`
          }
        </div>
      </div>

      {/* Predictive Search Input */}
      <div className="flex justify-center mb-6">
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by character or realm..."
          className="px-4 py-2 rounded-lg border border-border bg-surface text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 w-full max-w-xs"
        />
        {search && (
          <button
            className="ml-2 px-3 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600"
            onClick={() => setSearch('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>
      {loading ? (
        <div className="text-center text-text-secondary">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-400">{error}</div>
      ) : (
        <>
          {filteredMembers.length === 0 && (
            <p className="text-center text-text-secondary mb-6 max-w-xl mx-auto">
              {selectedPeriod === 'midnight_s2'
                ? 'No scores to show yet, or no guild members match your search.'
                : 'No entries match your search.'}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-surface rounded-xl shadow-lg">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Class</th>
                  <th className="px-4 py-2 text-left">Spec</th>
                  <th className="px-4 py-2 text-left">Score</th>
                  <th className="px-4 py-2 text-left">Profile</th>
                </tr>
              </thead>
              <tbody>
                {pagedMembers.map((m) => {
                  return (
                    <tr key={m.name + m.realm}>
                      <td className="px-4 py-2 font-bold">{m.rank}</td>
                      <td className="px-4 py-2">{m.name}</td>
                      <td className="px-4 py-2">
                        {capitalize(m.class.replace(/-/g, ' '))}
                      </td>
                      <td className="px-4 py-2 flex items-center gap-2">
                        <SpecIcon className={m.class} spec={m.spec} />
                        {m.spec}
                      </td>
                      <td className="px-4 py-2 font-semibold text-cyan-300">{m.mythic_plus_score.toFixed(1)}</td>
                      <td className="px-4 py-2">
                        <a href={m.profile_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline hover:text-cyan-200">Profile</a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                className="btn px-4 py-2 rounded font-bold"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span className="text-cyan-300 font-semibold">Page {page} of {totalPages}</span>
              <button
                className="btn px-4 py-2 rounded font-bold"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
    </div>
  );
};

export default MythicPlus; 